import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ModalBase from '../../../components/ModalBase';
import { PerformanceThresholds, DEFAULT_THRESHOLDS } from '../types';
import './OfferPerformanceSettings.scss';

// Re-export for backwards compatibility
export type { PerformanceThresholds };
export { DEFAULT_THRESHOLDS };

interface Props {
  thresholds: PerformanceThresholds;
  onSave: (thresholds: PerformanceThresholds) => void;
  onClose: () => void;
}

const OfferPerformanceSettings: React.FC<Props> = ({ thresholds, onSave, onClose }) => {
  const { t } = useTranslation();
  const [localThresholds, setLocalThresholds] = useState<PerformanceThresholds>(thresholds);

  const handleChange = (
    level: keyof PerformanceThresholds,
    field: 'minAvgRevenue' | 'minProductsSold',
    value: string
  ) => {
    const numValue = parseFloat(value) || 0;
    setLocalThresholds(prev => ({
      ...prev,
      [level]: {
        ...prev[level],
        [field]: numValue,
      },
    }));
  };

  const handleSave = () => {
    onSave(localThresholds);
    onClose();
  };

  const handleReset = () => {
    setLocalThresholds(DEFAULT_THRESHOLDS);
  };

  return (
    <ModalBase>
      <div className="settings-modal-content">
        <button className="close-btn" onClick={onClose}>×</button>

        <h2>{t('offersdashboard.settings.title')}</h2>
        <p className="settings-description">{t('offersdashboard.settings.description')}</p>

        <div className="settings-grid">
          {/* Excellent */}
          <div className="setting-group excellent">
            <h3>
              <span className="badge excellent">{t('productsdashboard.performance-rating.excellent')}</span>
            </h3>
            <div className="setting-row">
              <label>{t('offersdashboard.settings.min-avg-revenue')}</label>
              <div className="input-group">
                <span className="currency-symbol">€</span>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={localThresholds.excellent.minAvgRevenue}
                  onChange={(e) => handleChange('excellent', 'minAvgRevenue', e.target.value)}
                />
              </div>
            </div>
            <div className="setting-row">
              <label>{t('offersdashboard.settings.min-products-sold')}</label>
              <input
                type="number"
                step="1"
                min="0"
                value={localThresholds.excellent.minProductsSold}
                onChange={(e) => handleChange('excellent', 'minProductsSold', e.target.value)}
              />
            </div>
            <p className="condition-note">{t('offersdashboard.settings.and-condition')}</p>
          </div>

          {/* Good */}
          <div className="setting-group good">
            <h3>
              <span className="badge good">{t('productsdashboard.performance-rating.good')}</span>
            </h3>
            <div className="setting-row">
              <label>{t('offersdashboard.settings.min-avg-revenue')}</label>
              <div className="input-group">
                <span className="currency-symbol">€</span>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={localThresholds.good.minAvgRevenue}
                  onChange={(e) => handleChange('good', 'minAvgRevenue', e.target.value)}
                />
              </div>
            </div>
            <div className="setting-row">
              <label>{t('offersdashboard.settings.min-products-sold')}</label>
              <input
                type="number"
                step="1"
                min="0"
                value={localThresholds.good.minProductsSold}
                onChange={(e) => handleChange('good', 'minProductsSold', e.target.value)}
              />
            </div>
            <p className="condition-note">{t('offersdashboard.settings.or-condition')}</p>
          </div>

          {/* Average */}
          <div className="setting-group average">
            <h3>
              <span className="badge average">{t('productsdashboard.performance-rating.average')}</span>
            </h3>
            <div className="setting-row">
              <label>{t('offersdashboard.settings.min-avg-revenue')}</label>
              <div className="input-group">
                <span className="currency-symbol">€</span>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={localThresholds.average.minAvgRevenue}
                  onChange={(e) => handleChange('average', 'minAvgRevenue', e.target.value)}
                />
              </div>
            </div>
            <div className="setting-row">
              <label>{t('offersdashboard.settings.min-products-sold')}</label>
              <input
                type="number"
                step="1"
                min="0"
                value={localThresholds.average.minProductsSold}
                onChange={(e) => handleChange('average', 'minProductsSold', e.target.value)}
              />
            </div>
            <p className="condition-note">{t('offersdashboard.settings.or-condition')}</p>
          </div>

          {/* Poor (automatic) */}
          <div className="setting-group poor">
            <h3>
              <span className="badge poor">{t('productsdashboard.performance-rating.poor')}</span>
            </h3>
            <p className="auto-note">{t('offersdashboard.settings.poor-automatic')}</p>
          </div>
        </div>

        <div className="modal-actions">
          <button className="reset-btn" onClick={handleReset}>
            {t('offersdashboard.settings.reset-defaults')}
          </button>
          <button className="cancel-btn" onClick={onClose}>
            {t('offersdashboard.settings.cancel')}
          </button>
          <button className="save-btn" onClick={handleSave}>
            {t('offersdashboard.settings.save')}
          </button>
        </div>
      </div>
    </ModalBase>
  );
};

export default OfferPerformanceSettings;
